import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * 사용자 인증 및 정보 관리 Store
 * 
 * @description
 * - 로그인 후 토큰 및 사용자 정보를 관리합니다.
 * - JWT(idToken)를 디코딩하여 사용자 정보를 추출합니다.
 * - pinia-plugin-persistedstate로 새로고침에도 유지됩니다.
 * 
 * @example
 * // 사용 예시
 * const userStore = useUserStore()
 * 
 * // 로그인 후 토큰 저장
 * userStore.setTokens({ accessToken, idToken, refreshToken })
 * 
 * // 사용자 정보 접근
 * userStore.user.name
 * userStore.isLoggedIn
 * 
 * // 로그아웃
 * userStore.logout()
 */
export const useUserStore = defineStore('user', () => {
  // ===== 상태 =====
  
  /** Access Token (API 호출용) */
  const accessToken = ref(null)
  
  /** ID Token (사용자 정보 포함) */
  const idToken = ref(null)
  
  /** Refresh Token (토큰 갱신용) */
  const refreshToken = ref(null)
  
  /** 사용자 정보 (idToken에서 디코딩) */
  const user = ref({
    id: null,           // 사용자 고유 ID (sub)
    loginId: null,      // 로그인 아이디
    name: null,         // 이름
    email: null,        // 이메일
    identityVerifiedMember: null, // 본인인증 회원 여부
    // 필요한 필드 추가
  })

  // ===== Computed =====
  
  /** 로그인 여부 */
  const isLoggedIn = computed(() => !!accessToken.value)
  
  /** 토큰 만료 여부 확인 */
  const isTokenExpired = computed(() => {
    if (!idToken.value) return true
    try {
      const payload = parseJwt(idToken.value)
      const now = Math.floor(Date.now() / 1000)
      return payload.exp < now
    } catch {
      return true
    }
  })

  // ===== Actions =====

  /**
   * JWT 토큰 디코딩 (Base64URL → JSON)
   * @param {string} token - JWT 토큰
   * @returns {Object} 디코딩된 payload
   */
  function parseJwt(token) {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      return {}
    }
  }

  /**
   * 토큰 저장 및 사용자 정보 추출
   * @param {Object} tokens - 토큰 객체
   * @param {string} tokens.accessToken - Access Token
   * @param {string} tokens.idToken - ID Token
   * @param {string} [tokens.refreshToken] - Refresh Token (선택)
   */
  function setTokens(tokens) {
    accessToken.value = tokens.accessToken
    idToken.value = tokens.idToken
    if (tokens.refreshToken) {
      refreshToken.value = tokens.refreshToken
    }

    // idToken에서 사용자 정보 추출
    if (tokens.idToken) {
      const payload = parseJwt(tokens.idToken)

      user.value = {
        id: payload.mbrUnqId || payload.sub || null,           // 회원 고유 ID
        loginId: payload.sub || null,                           // 로그인 아이디 (sub에 있음)
        name: payload.name || payload.mbrName || null,          // 이름
        email: payload.email || null,                           // 이메일 (없을 수 있음)
        roles: payload.roles || [],                             // 권한 목록
        identityVerifiedMember: payload.identity_verified_member ?? null,
        // 필요시 추가 필드
      }

      if (user.value.identityVerifiedMember === true || user.value.identityVerifiedMember === false) {
        sessionStorage.setItem(
          'identityVerifiedMember',
          String(user.value.identityVerifiedMember)
        )
      } else {
        sessionStorage.removeItem('identityVerifiedMember')
      }
    }

    // sessionStorage에도 저장 (기존 코드 호환)
    sessionStorage.setItem('accessToken', tokens.accessToken)
    sessionStorage.setItem('idToken', tokens.idToken)
    if (tokens.refreshToken) {
      sessionStorage.setItem('refreshToken', tokens.refreshToken)
    }
  }

  /**
   * 사용자 정보 직접 설정 (API 응답 등에서 사용)
   * @param {Object} userData - 사용자 정보
   */
  function setUser(userData) {
    user.value = {
      ...user.value,
      ...userData
    }
  }

  /**
   * 로그아웃 - 모든 인증 정보 초기화
   */
  function logout() {
    accessToken.value = null
    idToken.value = null
    refreshToken.value = null
    user.value = {
      id: null,
      loginId: null,
      name: null,
      email: null,
      identityVerifiedMember: null,
    }

    // sessionStorage 정리
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('idToken')
    sessionStorage.removeItem('refreshToken')
    sessionStorage.removeItem('code_verifier')
    sessionStorage.removeItem('code_challenge')
    sessionStorage.removeItem('oauth_state')
    sessionStorage.removeItem('pkce_code')
    sessionStorage.removeItem('identityVerifiedMember')
  }

  /**
   * sessionStorage에서 토큰 복원 (앱 초기화 시)
   * @returns {boolean} 복원 성공 여부
   */
  function restoreFromStorage() {
    const storedAccessToken = sessionStorage.getItem('accessToken')
    const storedIdToken = sessionStorage.getItem('idToken')
    const storedRefreshToken = sessionStorage.getItem('refreshToken')
    const storedIdentityVerifiedMember = sessionStorage.getItem('identityVerifiedMember')

    if (storedAccessToken && storedIdToken) {
      setTokens({
        accessToken: storedAccessToken,
        idToken: storedIdToken,
        refreshToken: storedRefreshToken
      })
      
      // 토큰 만료 확인
      if (isTokenExpired.value) {
        logout()
        return false
      }
      
      return true
    }
    if (storedIdentityVerifiedMember !== null) {
      user.value = {
        ...user.value,
        identityVerifiedMember: storedIdentityVerifiedMember === 'true'
      }
    }
    return false
  }

  /**
   * Access Token 가져오기 (API 호출용)
   * @returns {string|null} Access Token
   */
  function getAccessToken() {
    return accessToken.value
  }

  /**
   * Authorization 헤더 생성
   * @returns {Object} { Authorization: 'Bearer xxx' }
   */
  function getAuthHeader() {
    if (!accessToken.value) return {}
    return {
      Authorization: `Bearer ${accessToken.value}`
    }
  }

  return {
    // State
    accessToken,
    idToken,
    refreshToken,
    user,
    
    // Computed
    isLoggedIn,
    isTokenExpired,
    
    // Actions
    parseJwt,
    setTokens,
    setUser,
    logout,
    restoreFromStorage,
    getAccessToken,
    getAuthHeader,
  }
}, {
  persist: {
    // 선택적: 특정 필드만 persist하거나 storage 변경 가능
    // storage: sessionStorage, // localStorage 대신 sessionStorage 사용
    // paths: ['accessToken', 'idToken', 'refreshToken', 'user']
  }
})
